import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, X, Zap } from 'lucide-react';

// Credit packs — deliberately small, cheap top-ups so a student can keep
// learning without committing to a full plan. Wire to real Razorpay later.
const PACKS = [
    { id: 'small', credits: 25, price: 49, tag: 'Just enough' },
    { id: 'popular', credits: 100, price: 149, tag: 'Most popular', highlighted: true },
    { id: 'big', credits: 300, price: 349, tag: 'Best value' },
];

const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

/**
 * PricingModal — portal-based tier picker triggered by CreditNotice.
 * Buying is currently MOCKED (no real Razorpay call); onPurchased fires with
 * the selected pack so the parent can grant credits locally.
 */
export default function PricingModal({ open, onClose, onPurchased }) {
    const [checkingOut, setCheckingOut] = useState(null);

    async function buy(pack) {
        setCheckingOut(pack.id);
        try {
            // MOCKED — simulate a Razorpay flow. Real integration pending user API key.
            await new Promise((r) => setTimeout(r, 900));
            onPurchased?.(pack);
        } finally {
            setCheckingOut(null);
        }
    }

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    key="pricing-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[330] flex items-center justify-center p-4"
                    style={{ background: 'rgba(11,15,7,0.72)', backdropFilter: 'blur(6px)' }}
                    data-testid="pricing-modal"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ y: 30, opacity: 0, scale: 0.96 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.83, 0, 0.17, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-3xl rounded-lg overflow-hidden max-h-[92vh] flex flex-col"
                        style={{
                            background:
                                'linear-gradient(160deg, rgba(255,255,255,0.98) 0%, rgba(245,240,255,0.96) 100%)',
                            border: '1px solid rgba(183,156,255,0.55)',
                            boxShadow: '0 40px 80px -25px rgba(11,15,7,0.65)',
                        }}
                    >
                        {/* Header */}
                        <div className="flex items-start justify-between p-6 border-b border-[color:var(--moss-deep)]/15">
                            <div>
                                <div className="mono text-[9px] tracking-[0.24em] text-[#7A5FCB]">
                                    METIS · TOP UP
                                </div>
                                <h3 className="display text-[color:var(--ink)] text-2xl mt-1">
                                    Pick a credit pack.
                                </h3>
                                <p className="text-[13px] text-[color:var(--ink-soft)] mt-1">
                                    One credit = one Metis conversation, lesson, or quiz. Cancel any time.
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                data-testid="pricing-modal-close"
                                className="p-1.5 rounded-sm hover:bg-[color:var(--moss-deep)]/10 text-[color:var(--moss-deep)]"
                                aria-label="Close"
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div className="grid sm:grid-cols-3 gap-4 p-6 overflow-y-auto book-scroll">
                            {PACKS.map((p) => (
                                <div
                                    key={p.id}
                                    data-testid={`pack-${p.id}`}
                                    className={`relative rounded-sm border p-5 flex flex-col ${
                                        p.highlighted
                                            ? 'border-[#B79CFF] bg-[color:var(--lavender)]/25'
                                            : 'border-[color:var(--moss-deep)]/30 bg-white/60'
                                    }`}
                                >
                                    {p.highlighted && (
                                        <div className="absolute -top-2 left-4 mono text-[9px] bg-[#B79CFF] text-[color:var(--moss-dark)] px-2 py-0.5 rounded-sm font-semibold">
                                            {p.tag.toUpperCase()}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-1.5 mono text-[10px] text-[color:var(--moss-deep)]">
                                        <Zap size={11} className="text-[#7A5FCB]" fill="currentColor" />
                                        {p.credits} CREDITS
                                    </div>
                                    <div className="display text-[color:var(--ink)] text-3xl mt-2 tabular-nums whitespace-nowrap">
                                        {fmt(p.price)}
                                    </div>
                                    {!p.highlighted && (
                                        <div className="mono text-[10px] text-[color:var(--moss-deep)]/80 mt-1">
                                            {p.tag}
                                        </div>
                                    )}
                                    <ul className="mt-4 space-y-1.5 flex-1 text-[12px] text-[color:var(--ink)]">
                                        <li className="flex items-start gap-2">
                                            <Check size={12} className="mt-[3px] text-[#7A5FCB]" /> Metis chat + voice
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={12} className="mt-[3px] text-[#7A5FCB]" /> Acropolis lessons
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <Check size={12} className="mt-[3px] text-[#7A5FCB]" /> 10-min speed tests
                                        </li>
                                    </ul>
                                    <button
                                        onClick={() => buy(p)}
                                        disabled={checkingOut === p.id}
                                        data-testid={`buy-pack-${p.id}`}
                                        className={`mt-4 w-full py-2.5 rounded-sm text-[11px] mono font-semibold ${
                                            p.highlighted
                                                ? 'bg-[color:var(--moss-dark)] text-[color:var(--parchment)]'
                                                : 'border border-[color:var(--moss-deep)] text-[color:var(--moss-deep)]'
                                        }`}
                                    >
                                        {checkingOut === p.id
                                            ? 'Processing…'
                                            : (
                                                <span className="flex items-center justify-center gap-1">
                                                    <Sparkles size={11} /> Top up
                                                </span>
                                            )}
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="px-6 pb-5 mono text-[9px] text-[color:var(--moss-deep)]/70">
                            Payments powered by Razorpay · <span className="italic">Live checkout MOCKED until API keys are provided.</span>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body,
    );
}
